import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BiosenseSignalMonitor from './BiosenseSignalMonitor';
import SettingsBars from './SettingsBars';
import Login from './login/Login';
import LoginDevice from './login/LoginDevice';
import KeyLicenseList from '../pages/KeyLicenseList';
import Dashboard from '../pages/Dashboard';
import Settings from '../pages/Settings';
import ChangePassword from '../pages/ChangePassword';
import Profile from '../pages/Profile';
import Terms from '../pages/Terms';
import Privacy from '../pages/Privacy';
import Disclaimer from '../pages/Disclaimer';
import About from '../pages/About';
import Results from '../pages/Results';
import MyVitals from '../pages/MyVitals';
import Scan from '../pages/Scan';
import EnhancedScan from '../pages/EnhancedScan';
import FullScan from '../pages/FullScan';
import LicenseInfo from '../pages/LicenseInfo';
import Patient from '../pages/Patient';
import HealthAssessment from '../pages/HealthAssessment';
import { Flex } from './shared/Flex';
import { useCameras, useDisableZoom } from '../hooks';
import UAParser from 'ua-parser-js';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import VisualHealthAnalysis from 'pages/VisualHealth/VisualHealthAnalysis';
import SuccessAssessment from 'pages/SuccessAssessment';

const Container = styled(Flex) <{ isSettingsOpen: boolean }>`
  height: 100%;
  width: 100%;
  position: relative;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: ${({ isSettingsOpen }) =>
    isSettingsOpen ? 'rgba(0, 0, 0, 0.5)' : 'transparent'};
`;

// ✅ Define PrivateRoute **outside** the styled component
const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  return isAuthenticated ? element : <Navigate to="/login" />;
};

const App = () => {
  const cameras = useCameras();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [cameraId, setCameraId] = useState<string>();
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [isMobile] = useState(
    UAParser(navigator.userAgent).device.type === 'mobile',
  );

  useDisableZoom();

  const onSettingsClickedHandler = useCallback((event) => {
    const settingsBars = document.getElementById('settingsBars');
    const isSettingsButtonClicked = event.target.id === 'settingsButton';
    const isInsideSettingsClicked =
      settingsBars?.contains(event.target as Node) || isSettingsButtonClicked;

    if (!isInsideSettingsClicked) {
      setIsSettingsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', onSettingsClickedHandler);
    return () => {
      document.removeEventListener('click', onSettingsClickedHandler);
    };
  }, [onSettingsClickedHandler]);

  const updateLicenseStatus = useCallback((valid) => {
    setIsLicenseValid(valid);
  }, []);

  const toggleSettingsClick = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  const handleCloseSettings = useCallback(({ cameraId }) => {
    setCameraId(cameraId);
    setIsSettingsOpen(false);
  }, []);

  useEffect(() => {
    if (!cameras?.length) return;
    setCameraId(cameras[0].deviceId);
  }, [cameras]);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={
          useSelector(selectIsAuthenticated) ? <Navigate to="/dashboard" replace /> : <Login />
        } />
        <Route path="/dashboard" element={<PrivateRoute element={<Dashboard />} />} />
        <Route path="/scan" element={<PrivateRoute element={<Scan />} />} />
        <Route path="/enhancedScan" element={<PrivateRoute element={<EnhancedScan />} />} />
        <Route path="/fullScan" element={<PrivateRoute element={<FullScan />} />} />
        <Route path="/patient" element={<PrivateRoute element={<Patient />} />} />
        <Route path="/health-assessment" element={<PrivateRoute element={<HealthAssessment />} />} />
        <Route path="/visual-health" element={<PrivateRoute element={<VisualHealthAnalysis />} />} />
        <Route path="/success-assessment" element={<PrivateRoute element={<SuccessAssessment />} />} />
        <Route path="/camera-scan" element={
          <PrivateRoute
            element={
              <Container isSettingsOpen={isSettingsOpen}>
                <BiosenseSignalMonitor
                  showMonitor={!(isMobile && isSettingsOpen)}
                  cameraId={cameraId}
                  onLicenseStatus={updateLicenseStatus}
                  onSettingsClick={toggleSettingsClick}
                  isSettingsOpen={isSettingsOpen}
                />
                <SettingsBars
                  open={isSettingsOpen}
                  onClose={handleCloseSettings}
                  cameras={cameras}
                  isLicenseValid={isLicenseValid}
                />
              </Container>
            }
          />
        } />
        <Route path="/licenseInfo" element={<PrivateRoute element={<LicenseInfo />} />} />
        <Route path="/results" element={<PrivateRoute element={<Results />} />} />
        <Route path="/vitals" element={<PrivateRoute element={<MyVitals />} />} />
        <Route path="/licenses" element={<PrivateRoute element={<KeyLicenseList />} />} />
        <Route path="/settings" element={<PrivateRoute element={<Settings />} />} />
        <Route path="/settings/change-password" element={<PrivateRoute element={<ChangePassword />} />} />
        <Route path="/settings/profile" element={<PrivateRoute element={<Profile />} />} />
        <Route path="/settings/terms" element={<PrivateRoute element={<Terms />} />} />
        <Route path="/settings/privacy" element={<PrivateRoute element={<Privacy />} />} />
        <Route path="/settings/disclaimer" element={<PrivateRoute element={<Disclaimer />} />} />
        <Route path="/settings/about" element={<PrivateRoute element={<About />} />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
